// PaymentHistoryFilters — reusable filter bar for Claim Payment History.
//
// RP-4 v2 (2026-05-08, per Kuhrt's product correction): the filter logic was
// drifting into a Reports.js-specific layout.  Extracted here as a single
// reusable component so the same filter shape works in:
//   1. Reports → Claim Payment History               (no claim lock)
//   2. Claim Workspace → Reports → Payment History   (claimIdLocked = current)
//   3. Future Operations → Accounting / Payment Queue
//   4. Future Admin/management payment audit reports
//
// Filters are combinable — no mutually exclusive "modes."  Claim search is
// the primary path (per Kuhrt 2026-05-08).  Payment Category is an explicit
// placeholder gated on the Phase 2 Peril/category tagging chunk landing.
//
// Privacy:
//   - Backend enforces access regardless of frontend filter state.
//   - canPickAdjuster gates the Lead Adjuster filter (Cap.MANAGE_USERS today).
//   - Ordinary adjusters never render the Lead Adjuster picker, never trigger
//     the user-list fetch — same pattern as RP-1.1's View-as gate.
//
// Props:
//   filters             — controlled state object (see DEFAULT_FILTERS)
//   onChange(next)      — partial-merge setter
//   claimIdLocked       — when set, claim search is hidden + filter forced
//   canPickAdjuster     — true if user has the cap to pick another adjuster
//   adjusterList        — array of {id, full_name, lead_count, staff_count}
//                         loaded by parent (lazy)
//   facets              — {claim_numbers:[{claim_number, insured_name}]}
//                         loaded by parent based on current adjuster selection
//   onRun()             — called when user clicks Run Report
//   recordCountLabel    — optional string to render in the right gutter

import React from "react";

export const DEFAULT_FILTERS = {
  claim_number_like:  "",
  payment_method:     "",
  status:             "",          // "" = All; deliberately not defaulting Open
  payment_category:   "",          // placeholder, ignored backend-side until Phase 2 lift
  date_from:          "",
  date_to:            "",
  lead_adjuster_id:   null,        // null = self / all (admin)
  insured_company_id: null,        // null = All; numeric companies.id when picked
  group_by:           "none",      // none | claim
};

export default function PaymentHistoryFilters({
  filters, onChange,
  claimIdLocked = null,
  canPickAdjuster = false,
  adjusterList = null,
  facets = null,
  onRun,
  recordCountLabel = "",
}) {
  // Partial-merge setter so callers can do set({status:"Open"}) without
  // restating the whole object.
  const set = patch => onChange({ ...filters, ...patch });

  return (
    <div className="search-bar" style={{marginBottom:16,flexWrap:"wrap",gap:8}}>
      {/* Claim search — primary filter.  Hidden when locked to a specific claim. */}
      {!claimIdLocked && (
        <>
          <input type="text"
                 list="ph-claim-numbers"
                 value={filters.claim_number_like}
                 onChange={e => set({ claim_number_like: e.target.value })}
                 placeholder="Claim # / Insured contains…"
                 title="Type or pick — searches claim number and insured field"
                 style={{padding:"6px 8px",border:"1px solid var(--border)",borderRadius:4,
                         width:230,fontFamily:"JetBrains Mono,monospace",fontSize:12}}/>
          <datalist id="ph-claim-numbers">
            {(facets?.claim_numbers || []).map(c => (
              <option key={c.claim_number} value={c.claim_number}>{c.insured_name || ""}</option>
            ))}
          </datalist>
        </>
      )}

      {/* Payment date range. */}
      <span style={{fontSize:11,color:"var(--text3)",alignSelf:"center"}}>Pay date:</span>
      <input type="date" value={filters.date_from}
             onChange={e => set({ date_from: e.target.value })}
             style={{padding:"6px 8px",border:"1px solid var(--border)",borderRadius:4}}
             title="Payment date from"/>
      <span style={{fontSize:12,color:"var(--text3)",alignSelf:"center"}}>to</span>
      <input type="date" value={filters.date_to}
             onChange={e => set({ date_to: e.target.value })}
             style={{padding:"6px 8px",border:"1px solid var(--border)",borderRadius:4}}
             title="Payment date to"/>

      {/* Lead adjuster — capability-gated.  Replaces the older "View-as" mode. */}
      {canPickAdjuster && (
        <select value={filters.lead_adjuster_id || ""}
                onChange={e => set({ lead_adjuster_id: e.target.value ? Number(e.target.value) : null })}
                title="Filter to one adjuster's claims (admin)"
                style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white"}}>
          <option value="">All adjusters</option>
          {adjusterList === null
            ? <option disabled>Loading…</option>
            : adjusterList.map(u => (
                <option key={u.id} value={u.id}>
                  {u.full_name} ({u.lead_count} lead{u.staff_count ? ` · ${u.staff_count} staff` : ""})
                </option>
              ))}
        </select>
      )}

      {/* Status — default "" (All).  Operational surfaces can preset to "Open". */}
      <select value={filters.status}
              onChange={e => set({ status: e.target.value })}
              style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white"}}
              title="Claim status">
        <option value="">All status</option>
        <option value="Open">Open</option>
        <option value="Closed">Closed</option>
        <option value="On Hold">On Hold</option>
        <option value="Litigation">Litigation</option>
      </select>

      {/* Insured — RP-4-FOLLOWUP-INSURED-FILTER (2026-05-12).  Activated
          post INSURED-LIFT-1 + RECONCILE-FK: claim_parties role='Insured'
          is durably maintained (1944+ rows, FK + type tagged at sync time).
          Options sourced via the facets payload — scope-aware so a self-only
          viewer sees only Insureds on their own claims. */}
      <select value={filters.insured_company_id || ""}
              onChange={e => set({ insured_company_id: e.target.value ? Number(e.target.value) : null })}
              title="Filter to claims with this Insured (policy-holding company)"
              style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white"}}>
        <option value="">All Insureds</option>
        {facets?.insured_options === undefined
          ? <option disabled>Loading…</option>
          : (facets?.insured_options || []).map(o => (
              <option key={o.company_id} value={o.company_id}>{o.company_name}</option>
            ))}
      </select>

      {/* Payment Category — Phase 2 Peril/category tagging dependency.  Disabled
          v1 placeholder so the slot is visible and design-locked. */}
      <select value={filters.payment_category} disabled
              title="Phase 2 — peril/category tagging lift"
              style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,
                      background:"#f3f4f6",color:"var(--text3)",cursor:"not-allowed",opacity:0.7}}>
        <option value="">Category (Phase 2)</option>
      </select>

      {/* Payment method. */}
      <select value={filters.payment_method}
              onChange={e => set({ payment_method: e.target.value })}
              style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,background:"white"}}
              title="Payment method">
        <option value="">All methods</option>
        <option value="cheque">Cheque</option>
        <option value="eft">EFT</option>
        <option value="ach">ACH</option>
        <option value="wire">Wire</option>
        <option value="other">Other</option>
      </select>

      {/* Group-by — secondary, visually unobtrusive. */}
      <select value={filters.group_by}
              onChange={e => set({ group_by: e.target.value })}
              style={{padding:"7px 10px",border:"1px solid var(--border)",borderRadius:4,
                      background:"white",color:"var(--text2)",fontSize:12}}
              title="Group results">
        <option value="none">No grouping</option>
        <option value="claim">Group by Claim</option>
      </select>

      <button className="btn btn-blue" onClick={onRun}>Run Report</button>
      {recordCountLabel && (
        <span style={{marginLeft:"auto",fontSize:12,color:"var(--text3)",alignSelf:"center"}}>
          {recordCountLabel}
        </span>
      )}
    </div>
  );
}

// Helper: build the URLSearchParams that the backend endpoint expects.
// Centralised so the same shape works for the standalone Reports view and the
// future per-claim Reports tab.  `claimIdLocked` (a numeric claim_id) takes
// precedence over the user-typed claim_number_like.
export function buildPaymentHistoryQuery(filters, claimIdLocked = null) {
  const p = new URLSearchParams();
  if (claimIdLocked) {
    p.set("claim_id", String(claimIdLocked));
  } else if (filters.claim_number_like && filters.claim_number_like.trim()) {
    p.set("claim_number_like", filters.claim_number_like.trim());
  }
  if (filters.status)           p.set("status",         filters.status);
  if (filters.payment_method)   p.set("payment_method", filters.payment_method);
  if (filters.date_from)        p.set("date_from",      filters.date_from);
  if (filters.date_to)          p.set("date_to",        filters.date_to);
  if (filters.lead_adjuster_id) p.set("user_id",        String(filters.lead_adjuster_id));
  if (filters.insured_company_id) p.set("insured_company_id", String(filters.insured_company_id));
  // payment_category intentionally omitted until Phase 2 backend support lands.
  return p;
}
